import {
  Button,
  Card,
  EmptyStatus,
  Form,
  FormButton,
  FormField,
  IconButton,
  Segment,
  Separator,
  Switch,
  openConfirmDialog,
  showNotification,
  type FormControl,
  type FormSubmitHandler,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {useController, useFormState, useWatch, type UseFormReturn} from 'react-hook-form';

import {isNil, uniqBy} from 'ramda';

import {
  newVehicleApi,
  type FeatureResponseBody,
  type OfferedTrimResponseBody,
  type PackageResponseBody,
  type TrimResponseBody,
} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {useGroupedNewTrims} from '../hooks/useGroupedNewTrims';
import {usePackageSave} from '../hooks/usePackageSave';
import {usePublishedPriceSave} from '../hooks/usePublishedPriceSave';
import {packageSchema} from '../schemas/packageSchema';
import {ITEM_CHANGE_CANCELLED} from '../types/PricedItemChangeOutcome';
import {handleCatalogueError, joinParts, t} from '../utils/catalogueUi';
import {isInPricelist} from '../utils/isInPricelist';
import {changesKeptPrice, memberRows} from '../utils/offeredTrimChanges';
import {
  changedTrimPrices,
  publishedOnePriceAtATime,
  toOfferedTrimRows,
  withStoredPrices,
} from '../utils/offeredTrimRows';
import {rebaseForm} from '../utils/rebaseForm';
import {statusWithPriceError} from '../utils/statusWithPriceError';
import {FeatureLibraryChoice} from './FeatureLibraryChoice';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {OfferedTrimsTable} from './OfferedTrimsTable';
import {RetryReadButton, type ItemReadAgain} from './RetryReadButton';

type BuilderValues = z.infer<typeof packageSchema>;
type Control = FormControl<BuilderValues>;

type Props = {
  modelYearId: string;
  item?: PackageResponseBody;
  features: FeatureResponseBody[];
  trims: Pick<TrimResponseBody, 'id' | 'name' | 'publicationStatus'>[];
  currency: string;
  canViewCost: boolean;
  onClose: () => void;
};

/** Full-width editor for one package: basics, its equipment, and its price on each trim it is sold on. */
export function PackageBuilder(props: Props) {
  const [item, setItem] = useState(props.item);
  const [getPackage, packageRead] = newVehicleApi.useLazyGetPackageQuery();
  const vatRate = useCatalogueVatRate(props.modelYearId);
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const [libraryFeatures, setLibraryFeatures] = useState<FeatureResponseBody[]>([]);
  const features = uniqBy(({id}) => id, [...props.features, ...libraryFeatures]);
  // The package can be offered on a trim that the open group creates: it then joins the group.
  const groupedNewTrims = useGroupedNewTrims(props.modelYearId);
  const offerableTrims = [...props.trims, ...groupedNewTrims];
  const {persisted, save, reportFailure} = usePackageSave({
    ...props,
    item,
    features,
    trims: offerableTrims,
    groupedTrimIds: groupedNewTrims.map(({id}) => id),
    publishedPrice,
  });
  const toValues = (read: PackageResponseBody | undefined): BuilderValues => ({
    name: read?.name ?? '',
    factoryCode: read?.codes[0] ?? '',
    description: read?.description ?? '',
    status: read?.status === 'active' ? 'active' : 'draft',
    memberFeatureIds: read?.memberFeatureIds ?? [],
    trims: toOfferedTrimRows(props.trims, read?.trims ?? []),
    packageOnlyFeatureIds: props.features
      .filter((feature) => !feature.standaloneOrderable)
      .map(({id}) => id),
  });
  const defaultValues = toValues(item);

  const storedTrims = () => persisted.current?.trims ?? item?.trims ?? [];

  const submit: FormSubmitHandler<BuilderValues> = async (values, setErrors, _reset, blocker) => {
    const checkpoint = persisted.current;
    const stored = storedTrims();
    const isStoredActive = isInPricelist.package((checkpoint ?? item)?.status ?? 'draft');
    // Only a row in the price list before and after keeps its price until an adjustment sets it (D102).
    const locked = memberRows(stored, props.trims, isStoredActive);
    const changed = changedTrimPrices(values.trims, locked);
    try {
      // A deactivation takes the rows out at their stored prices, so it cannot change one too (P2).
      if (
        isStoredActive &&
        values.status === 'draft' &&
        changed.length > 0 &&
        (await publishedPrice.readIsPublished())
      ) {
        setErrors([statusWithPriceError('trims')]);
        blocker?.reportApiFailure?.();
        return;
      }
      const result = await publishedPrice.saveRows({
        values,
        heldBackValues: {...values, trims: withStoredPrices(values.trims, locked)},
        storedValues: {...defaultValues, trims: toOfferedTrimRows(props.trims, stored)},
        changed,
        isSavePending: checkpoint !== null,
        scope: 'packageTrim',
        save,
        onDone: props.onClose,
      });
      match(result)
        .with('saved', () =>
          showNotification.success(t('general.notifications.changesSaved', 'Changes saved'))
        )
        // The pricelist version dialog, or the group, has already said what happened to the package.
        .with('savedWithVersion', 'grouped', () => undefined)
        .with('onePriceAtATime', () => {
          showNotification.error(publishedOnePriceAtATime(), {errorOrigin: 'business'});
          blocker?.reportApiFailure?.();
        })
        // Keeps the unsaved-changes blocker from leaving while a confirm is shown, or after the
        // new pricelist version was cancelled.
        .with('confirming', ITEM_CHANGE_CANCELLED, () => blocker?.reportApiFailure?.())
        .exhaustive();
    } catch (error) {
      reportFailure(error, checkpoint);
      blocker?.reportApiFailure?.();
    }
  };

  const readPackageAgain = (form: UseFormReturn<BuilderValues>) => {
    const packageId = persisted.current?.id ?? item?.id;
    return isNil(packageId)
      ? Promise.resolve()
      : getPackage({packageId}, false)
          .unwrap()
          .then((current) => {
            rebaseForm(form, toValues(current));
            setItem(current);
            if (persisted.current) {
              const {id, version, name, status, trims} = current;
              persisted.current = {id, version, name, status, trims};
            }
          })
          .catch(handleCatalogueError);
  };
  const setSamePrice = (
    priceNet: string,
    onCreated: () => void,
    form: UseFormReturn<BuilderValues>
  ) => {
    const packageId = persisted.current?.id ?? item?.id;
    const isStoredActive = isInPricelist.package((persisted.current ?? item)?.status ?? 'draft');
    // Only a changed price of a stored row in the price list needs a price adjustment; the form
    // fills the rows otherwise.
    return isNil(packageId) ||
      !changesKeptPrice(
        storedTrims(),
        props.trims,
        isStoredActive,
        form.getValues('trims'),
        priceNet
      )
      ? Promise.resolve(false)
      : publishedPrice.confirmAdjustment(
          {kind: 'set', value: priceNet, scope: 'package', resourceIds: [packageId]},
          {
            onDone: () => {
              onCreated();
              void readPackageAgain(form);
            },
          }
        );
  };

  return (
    <Form<BuilderValues>
      key={props.item?.id ?? 'new'}
      experimentalZodSchema={packageSchema}
      shouldWatchForUnsavedChanges
      defaultValues={defaultValues}
      onSubmit={submit}
    >
      {(control, form) => (
        <VStack spacing={4}>
          <BuilderHeader
            control={control}
            item={item}
            onClose={props.onClose}
            packageRead={{
              isFetching: packageRead.isFetching,
              isError: packageRead.isError,
              retry: () => void readPackageAgain(form),
            }}
          />
          <BuilderColumns>
            <BasicsCard control={control} />
            <ContentsCard
              control={control}
              features={features}
              yearFeatures={props.features}
              onAdd={(feature) => setLibraryFeatures((current) => [...current, feature])}
            />
            <TrimPricesCard
              control={control}
              trims={offerableTrims}
              stored={storedTrims()}
              currency={props.currency}
              canViewCost={props.canViewCost}
              vatRate={vatRate}
              onSamePrice={(priceNet, onCreated) => setSamePrice(priceNet, onCreated, form)}
            />
          </BuilderColumns>
          <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
        </VStack>
      )}
    </Form>
  );
}

// Full width like the rest of the app: basics | contents | a narrower summary, all one height so the
// row reads as one sheet. Laptops keep the summary beside a stacked pair; smaller screens stack all.
// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const BuilderColumns = styled.div`
  display: grid;
  gap: ${({theme}) => theme.getSize(4)};
  grid-template-columns: 3fr 3fr 2fr;
  align-items: stretch;

  @media ${({theme}) => theme.devices.notebook} {
    grid-template-columns: 2fr 1fr;

    & > :nth-child(3) {
      grid-column: 2;
      grid-row: 1 / span 2;
    }
  }

  @media ${({theme}) => theme.devices.tablet}, ${({theme}) => theme.devices.mobile} {
    grid-template-columns: 1fr;
  }
`;

function BuilderHeader(props: {
  control: Control;
  item?: PackageResponseBody;
  onClose: () => void;
  packageRead: ItemReadAgain;
}) {
  const {isDirty} = useFormState({control: props.control});
  const close = () =>
    isDirty
      ? openConfirmDialog({
          text: t('entity.newVehicle.catalogue.discardPackageChanges', 'Discard unsaved changes?'),
          onConfirm: props.onClose,
        })
      : props.onClose();
  return (
    <HStack justify="space-between" align="flex-end" spacing={4} wrap>
      <VStack spacing={1} align="flex-start">
        <Button
          variant="link"
          size="small"
          leftIcon="navigation/arrow_back"
          title={t('entity.newVehicle.catalogue.backToPackages', 'All packages')}
          onClick={close}
        />
        <Heading size={3}>
          {props.item
            ? t('entity.newVehicle.catalogue.editPackageNamed', 'Edit package · {name}', {
                name: props.item.name,
              })
            : t('entity.newVehicle.catalogue.addPackage', 'Add package')}
        </Heading>
      </VStack>
      <HStack spacing={2}>
        <Button variant="secondary" title={t('general.actions.cancel', 'Cancel')} onClick={close} />
        <RetryReadButton read={props.packageRead} />
        <FormButton
          control={props.control}
          type="submit"
          variant="primary"
          isDisabled={props.packageRead.isFetching || props.packageRead.isError}
          isLoading={props.packageRead.isFetching}
          title={
            props.item
              ? t('general.actions.saveChanges', 'Save changes')
              : t('entity.newVehicle.catalogue.createPackage', 'Create package')
          }
        />
      </HStack>
    </HStack>
  );
}

function BasicsCard(props: {control: Control}) {
  const status = useController({control: props.control, name: 'status'});
  return (
    <Card title={t('entity.newVehicle.catalogue.packageBasics', 'Package basics')} isFullHeight>
      <VStack spacing={4}>
        <Grid columns={[1, 3]} spacing={3}>
          <GridItem span={[1, 2]}>
            <FormField
              control={props.control}
              name="name"
              type="text"
              label={t('general.labels.name', 'Name')}
              placeholder={t(
                'entity.newVehicle.catalogue.packageNamePlaceholder',
                'e.g. Winter pack'
              )}
              isRequired
            />
          </GridItem>
          <FormField
            control={props.control}
            name="factoryCode"
            type="text"
            label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          />
        </Grid>
        <FormField
          control={props.control}
          name="description"
          type="textarea"
          label={t('general.labels.description', 'Description')}
          helperText={t(
            'entity.newVehicle.catalogue.packageDescriptionHelp',
            'Shown to the customer in the configurator.'
          )}
          isRequired
        />
        <HStack spacing={3} align="center" wrap>
          <Segment
            label={t('general.labels.status', 'Status')}
            value={status.field.value}
            onChange={status.field.onChange}
            options={(['draft', 'active'] as const).map((value) => ({
              value,
              label: getNewVehicleEnumLabel('packageStatus', value, t),
            }))}
          />
          <Text size="xSmall" color="secondary">
            {t(
              'entity.newVehicle.catalogue.packageStatusHelp',
              'Only active packages appear in the configurator.'
            )}
          </Text>
        </HStack>
      </VStack>
    </Card>
  );
}

function ContentsCard(props: {
  control: Control;
  features: FeatureResponseBody[];
  yearFeatures: FeatureResponseBody[];
  onAdd: (feature: FeatureResponseBody) => void;
}) {
  const members = useController({control: props.control, name: 'memberFeatureIds'});
  const packageOnly = useController({control: props.control, name: 'packageOnlyFeatureIds'});
  const memberIds = members.field.value;
  const addMember = (feature: FeatureResponseBody) => {
    if (!feature.standaloneOrderable && !props.features.some(({id}) => id === feature.id)) {
      packageOnly.field.onChange([...packageOnly.field.value, feature.id]);
    }
    props.onAdd(feature);
    members.field.onChange([...memberIds, feature.id]);
  };
  const setOrderableAlone = (id: string, orderable: boolean) =>
    packageOnly.field.onChange(
      orderable
        ? packageOnly.field.value.filter((value) => value !== id)
        : [...packageOnly.field.value, id]
    );
  return (
    <Card
      isFullHeight
      title={t('entity.newVehicle.catalogue.packageContents', 'Package contents ({count})', {
        count: memberIds.length,
      })}
    >
      <VStack spacing={3}>
        {memberIds.length ? (
          <VStack role="list">
            {memberIds.map((id, index) => {
              const feature = props.features.find((item) => item.id === id);
              const isOrderableAlone = !packageOnly.field.value.includes(id);
              return (
                <VStack key={id} role="listitem">
                  <Show when={index > 0}>
                    <Separator spacing={0} />
                  </Show>
                  <HStack justify="space-between" align="center" spacing={3}>
                    <Box flex={1} minWidth={0} paddingVertical={2}>
                      <VStack spacing={0}>
                        <Text size="small" alternative>
                          {feature?.name ?? id}
                        </Text>
                        <Text size="xSmall" color="secondary">
                          {joinParts([
                            feature
                              ? getNewVehicleEnumLabel('featureCategory', feature.category, t)
                              : undefined,
                            feature
                              ? getNewVehicleEnumLabel('fitStage', feature.fitStage, t)
                              : undefined,
                            isOrderableAlone
                              ? undefined
                              : t('entity.newVehicle.rules.packageOnly', 'Package-only'),
                          ])}
                        </Text>
                      </VStack>
                    </Box>
                    <Show when={Boolean(feature)}>
                      <Switch
                        label={t('entity.newVehicle.catalogue.orderableAlone', 'Orderable alone')}
                        value={isOrderableAlone}
                        onChange={(value) => setOrderableAlone(id, value)}
                      />
                    </Show>
                    <IconButton
                      icon="navigation/close"
                      size="small"
                      aria-label={t(
                        'entity.newVehicle.catalogue.removeFromPackage',
                        'Remove {name}',
                        {
                          name: feature?.name ?? id,
                        }
                      )}
                      onClick={() =>
                        members.field.onChange(memberIds.filter((value) => value !== id))
                      }
                    />
                  </HStack>
                </VStack>
              );
            })}
          </VStack>
        ) : (
          <EmptyStatus
            headline={t('entity.newVehicle.catalogue.emptyPackageContents', 'No equipment yet')}
            subheadline={t(
              'entity.newVehicle.catalogue.emptyPackageContentsHint',
              'Add the equipment this package bundles.'
            )}
          />
        )}
        <FeatureLibraryChoice
          yearFeatures={props.yearFeatures}
          excludedIds={memberIds}
          label={t('entity.newVehicle.catalogue.addEquipmentToPackage', 'Add equipment…')}
          placeholder={t('entity.newVehicle.catalogue.addEquipmentToPackage', 'Add equipment…')}
          onPick={addMember}
        />
        <Show when={memberIds.length > 0}>
          <Text size="xSmall" color="secondary">
            {t(
              'entity.newVehicle.catalogue.orderableAloneHelp',
              'Switching off “Orderable alone” makes the item package-only in the equipment library, for every trim and package.'
            )}
          </Text>
        </Show>
      </VStack>
    </Card>
  );
}

function TrimPricesCard(props: {
  control: Control;
  trims: {id: string; name: string}[];
  stored: OfferedTrimResponseBody[];
  currency: string;
  canViewCost: boolean;
  vatRate?: string;
  onSamePrice: (priceNet: string, onCreated: () => void) => Promise<boolean>;
}) {
  const name = useWatch({control: props.control, name: 'name'});
  const trims = useController({control: props.control, name: 'trims'});
  return (
    <Card title={t('entity.newVehicle.catalogue.priceByTrim', 'Price by trim')} isFullHeight>
      <VStack spacing={3}>
        <Text size="xSmall" color="secondary">
          {t(
            'entity.newVehicle.catalogue.packageTrimsHelp',
            'The package is offered only on the trims switched on here, at its price on each. 0 means included.'
          )}
        </Text>
        <OfferedTrimsTable
          rows={trims.field.value}
          stored={props.stored}
          onChange={trims.field.onChange}
          trims={props.trims}
          itemName={name}
          currency={props.currency}
          vatRate={props.vatRate}
          canViewCost={props.canViewCost}
          error={trims.fieldState.error?.message}
          onSamePrice={props.onSamePrice}
          isDisabled={trims.formState.isSubmitting}
        />
      </VStack>
    </Card>
  );
}
